import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './Jobs.css';
import NavBarTop from '../components/NavBarTop';
import BottomSheet from '../components/BottomSheet';
import '../components/BottomSheet.css';

const Jobs = () => {
    const [careers, setCareers] = useState([]);
    const [jobTypes, setJobTypes] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCareers, setTotalCareers] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [selectedJob, setSelectedJob] = useState(null); // New state for detail view

    // Filters
    const [search, setSearch] = useState('');
    const [jobType, setJobType] = useState('');
    const [location, setLocation] = useState('');
    const [providerType, setProviderType] = useState('');
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);

    const BASE_URL = 'https://physiodirectory.in';

    // Removed initial mount fetch to prevent double loading. All fetches handled by filter effect below.

    useEffect(() => {
        if (!selectedJob) {
            const delayDebounceFn = setTimeout(() => {
                fetchData(1);
            }, 500);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [search, jobType, location, providerType, selectedJob]);

    const fetchData = async (page = 1) => {
        setLoading(true);
        try {
            const params = { page };
            if (search) params.search = search;
            if (jobType) params.job_type = jobType;
            if (location) params.location = location;
            if (providerType) params.provider_type = providerType;

            const response = await axios.get(`${BASE_URL}/api/careers`, { params });
            
            setCareers(response.data.careers.data);
            setTotalCareers(response.data.careers.total);
            setCurrentPage(response.data.careers.current_page);
            setLastPage(response.data.careers.last_page);
            setJobTypes(response.data.jobTypes);
            setLocations(response.data.locations);
        } catch (error) {
            console.error('Error fetching jobs data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage) {
            fetchData(newPage);
            window.scrollTo(0, 0);
        }
    };

    const clearFilters = () => {
        setSearch('');
        setJobType('');
        setLocation('');
        setProviderType('');
    };

    const handleApply = (type, value, e) => {
        if (e) e.stopPropagation();
        if (!value) return;
        if (type === 'call') {
            if (window.Native && window.Native.openExternal) {
                window.Native.openExternal({ url: `tel:${value}` });
            } else {
                window.location.href = `tel:${value}`;
            }
        } else if (type === 'whatsapp') {
            const cleanNumber = value.replace(/[^0-9]/g, '');
            const url = `https://wa.me/${cleanNumber}`;
            if (window.Native && window.Native.openExternal) {
                window.Native.openExternal({ url });
            } else {
                window.location.href = url;
            }
        } else if (type === 'email') {
            if (window.Native && window.Native.openExternal) {
                window.Native.openExternal({ url: `mailto:${value}` });
            } else {
                window.location.href = `mailto:${value}`;
            }
        } else if (type === 'website' || type === 'location') {
            if (window.Native && window.Native.openExternal) {
                window.Native.openExternal({ url: value });
            } else {
                window.open(value, '_blank');
            }
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
    };

    // Detail View Component
    if (selectedJob) {
        const provider = selectedJob.user || {};
        const providerType = provider.role ? provider.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Provider';
        let logoUrl = null;
        if (provider.role === 'clinic' && provider.clinic_profile?.logo_path) logoUrl = provider.clinic_profile.logo_path;
        else if (provider.role === 'rehab_center' && provider.rehab_center_profile?.logo_path) logoUrl = provider.rehab_center_profile.logo_path;

        return (
            <div className="mobile-page-container bg-light min-vh-100">
                {/* Top NavBar with Back and Title */}
                <NavBarTop title="Job Details" />
                {/* Banner & Header */}
                <div className="position-relative" style={{ height: '200px', background: 'var(--primary-color)' }}>
                    <img src={`${BASE_URL}/assets/images/pd_bgm.png`} alt="" className="w-100 h-100 object-fit-cover opacity-50" />
                </div>
                {/* Add gap below nav for spacing */}
                <div style={{height: '16px'}}></div>

                <div className="container px-3" style={{ marginTop: '-60px' }}>
                    <div className="card border-0 shadow rounded-4 mb-3">
                        <div className="card-body text-center pt-5 position-relative">
                            <div className="position-absolute top-0 start-50 translate-middle p-1 bg-white rounded-circle shadow-sm">
                                <div className="rounded-circle bg-light d-flex align-items-center justify-content-center overflow-hidden" style={{width: '80px', height: '80px'}}>
                                    {logoUrl ? <img src={`${BASE_URL}/storage/${logoUrl}`} alt="" className="w-100 h-100 object-fit-cover"/> : <i className="bi bi-building text-secondary fs-1"></i>}
                                </div>
                            </div>
                            <h4 className="fw-bold text-dark mb-1 mt-2">{selectedJob.job_title}</h4>
                            <p className="text-muted mb-2">{provider.name}</p>
                            <span className="badge bg-primary-subtle text-primary mb-3">{providerType}</span>
                            <div className="d-flex flex-wrap justify-content-center gap-2 mb-2">
                                {selectedJob.job_type && <span className="badge bg-light text-dark border"><i className="bi bi-briefcase me-1"></i>{selectedJob.job_type}</span>}
                                {selectedJob.location && <span className="badge bg-light text-dark border"><i className="bi bi-geo-alt me-1"></i>{selectedJob.location}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Job Description */}
                    <div className="card border-0 shadow-sm rounded-4 mb-3">
                        <div className="card-header bg-white border-bottom-0 pt-3 pb-0">
                            <h6 className="fw-bold text-dark mb-0">Job Description</h6>
                        </div>
                        <div className="card-body">
                            <p className="text-muted small mb-0" style={{ whiteSpace: 'pre-wrap' }}>{selectedJob.job_description}</p>
                        </div>
                    </div>

                    {/* Requirements & Details */}
                    <div className="card border-0 shadow-sm rounded-4 mb-3">
                        <div className="card-header bg-white border-bottom-0 pt-3 pb-0">
                            <h6 className="fw-bold text-dark mb-0">Requirements & Details</h6>
                        </div>
                        <div className="card-body">
                            <div className="row g-2">
                                <div className="col-6">
                                    <div className="p-2 bg-light rounded-3 h-100">
                                        <small className="d-block text-muted mb-1" style={{fontSize:'0.7rem'}}>EXPERIENCE</small>
                                        <div className="fw-semibold text-dark small">{selectedJob.experience_required || 'Not specified'}</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-2 bg-light rounded-3 h-100">
                                        <small className="d-block text-muted mb-1" style={{fontSize:'0.7rem'}}>QUALIFICATION</small>
                                        <div className="fw-semibold text-dark small">{selectedJob.qualification_required || 'Not specified'}</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-2 bg-light rounded-3 h-100">
                                        <small className="d-block text-muted mb-1" style={{fontSize:'0.7rem'}}>TOTAL VACANCIES</small>
                                        <div className="fw-semibold text-dark small">{selectedJob.vacancies || 'Not specified'}</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-2 bg-light rounded-3 h-100">
                                        <small className="d-block text-muted mb-1" style={{fontSize:'0.7rem'}}>MALE VACANCIES</small>
                                        <div className="fw-semibold text-dark small">{selectedJob.vacancies_male || 'Not specified'}</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-2 bg-light rounded-3 h-100">
                                        <small className="d-block text-muted mb-1" style={{fontSize:'0.7rem'}}>FEMALE VACANCIES</small>
                                        <div className="fw-semibold text-dark small">{selectedJob.vacancies_female || 'Not specified'}</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-2 bg-light rounded-3 h-100">
                                        <small className="d-block text-muted mb-1" style={{fontSize:'0.7rem'}}>APPLICATION DEADLINE</small>
                                        <div className="fw-semibold text-dark small">{selectedJob.application_deadline ? new Date(selectedJob.application_deadline).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}) : 'Not specified'}</div>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="p-2 bg-light rounded-3">
                                        <small className="d-block text-muted mb-1" style={{fontSize:'0.7rem'}}>SALARY</small>
                                        <div className="fw-semibold text-dark small">
                                            {selectedJob.salary_min && selectedJob.salary_max ? `₹${Number(selectedJob.salary_min).toLocaleString()} - ₹${Number(selectedJob.salary_max).toLocaleString()} /mo` :
                                             selectedJob.salary_min ? `₹${Number(selectedJob.salary_min).toLocaleString()}+ /mo` :
                                             selectedJob.salary_max ? `Up to ₹${Number(selectedJob.salary_max).toLocaleString()} /mo` : 'Not specified'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Apply Section */}
                    <div className="card border-0 shadow-sm rounded-4 mb-5">
                        <div className="card-header bg-primary text-white pt-3 pb-2 rounded-top-4">
                            <h6 className="fw-bold mb-0 text-white">Apply for this Position</h6>
                        </div>
                        <div className="card-body">
                            <p className="text-muted small mb-3">
                                If you are interested in this position, please apply by contacting the provider directly via phone call or WhatsApp.
                            </p>
                            <div className="d-grid gap-2">
                                {provider.mobile_number && (
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary rounded-pill"
                                        onClick={e => handleApply('call', provider.mobile_number, e)}
                                    >
                                        <i className="bi bi-telephone me-2"></i>Call Now
                                    </button>
                                )}
                                {provider.whatsapp_number && (
                                    <button
                                        type="button"
                                        className="btn btn-success text-white rounded-pill"
                                        onClick={e => handleApply('whatsapp', provider.whatsapp_number, e)}
                                    >
                                        <i className="bi bi-whatsapp me-2"></i>WhatsApp
                                    </button>
                                )}
                                {!provider.mobile_number && !provider.whatsapp_number && (
                                    <div className="alert alert-light text-center small text-muted">
                                        Contact details not available.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

        return (
                <div className="mobile-page-container">
                        {/* Top NavBar with Back and Title and Filter Button */}
                        <NavBarTop 
                            title="Jobs"
                            right={
                                <button className="btn btn-link p-0" style={{fontSize: '1.5rem'}} onClick={() => setFilterSheetOpen(true)}>
                                    <i className="bi bi-funnel"></i>
                                </button>
                            }
                        />
                        <BottomSheet open={filterSheetOpen} onClose={() => setFilterSheetOpen(false)} className="jobs-bottom-sheet">
                            <form onSubmit={e => e.preventDefault()} className="mb-3">
                                <label className="form-label fw-bold">Search</label>
                                <div className="search-input-wrapper">
                                    <i className="bi bi-search"></i>
                                    <input 
                                        type="text" 
                                        className="form-control modern-search-input" 
                                        placeholder="Search Jobs..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </div>
                            </form>
                            <div className="d-flex gap-2 mb-3">
                              <div style={{flex: 1}}>
                                <label className="form-label fw-bold">Location</label>
                                <select 
                                    className="form-select"
                                    value={location}
                                    onChange={e => setLocation(e.target.value)}
                                >
                                    <option value="">All Locations</option>
                                    {locations.map((loc, i) => <option key={i} value={loc}>{loc}</option>)}
                                </select>
                              </div>
                              <div style={{flex: 1}}>
                                <label className="form-label fw-bold">Type</label>
                                <select 
                                    className="form-select"
                                    value={jobType}
                                    onChange={e => setJobType(e.target.value)}
                                >
                                    <option value="">All Types</option>
                                    {jobTypes.map((type, i) => <option key={i} value={type}>{type}</option>)}
                                </select>
                              </div>
                            </div>
                            <label className="form-label fw-bold">Provider</label>
                            <input 
                                type="text"
                                className="form-control mb-2"
                                placeholder="Provider Type"
                                value={providerType}
                                onChange={e => setProviderType(e.target.value)}
                            />
                        </BottomSheet>

            <div className="container-fluid px-3 pt-2">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0 text-dark">Open Positions</h5>
                    <span className="text-muted small">{totalCareers} Jobs</span>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                         <div className="spinner-border text-primary" role="status"></div>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-3">
                        {careers.length > 0 ? (
                            careers.map(career => {
                                const provider = career.user || {};
                                const providerType = provider.role ? provider.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Provider';
                                let logoUrl = null;
                                // Simple logic for logo as before
                                if (provider.role === 'clinic' && provider.clinic_profile?.logo_path) logoUrl = provider.clinic_profile.logo_path;
                                else if (provider.role === 'rehab_center' && provider.rehab_center_profile?.logo_path) logoUrl = provider.rehab_center_profile.logo_path;

                                return (
                                    <div className="card border-0 shadow-sm rounded-4 p-3 job-card" key={career.id}>
                                        <div className="d-flex align-items-start">
                                            <div className="rounded-3 bg-light p-2 d-flex align-items-center justify-content-center me-3" style={{width: '50px', height: '50px', minWidth: '50px'}}>
                                                {logoUrl ? <img src={`${BASE_URL}/storage/${logoUrl}`} alt="" className="w-100 h-100 object-fit-contain"/> : <i className="bi bi-building text-secondary fs-4"></i>}
                                            </div>
                                            <div className="flex-grow-1">
                                                <h6 className="fw-bold mb-1 text-dark text-truncate-2" style={{fontSize: '1rem'}}>{career.job_title}</h6>
                                                <p className="text-muted small mb-1">{provider.name}</p>
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    {career.job_type && <span className="badge bg-light text-dark fw-normal border"><i className="bi bi-briefcase me-1"></i>{career.job_type}</span>}
                                                    {career.location && <span className="badge bg-light text-dark fw-normal border"><i className="bi bi-geo-alt me-1"></i>{career.location}</span>}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="mt-3 small text-muted bg-light p-2 rounded-3">
                                            {career.qualification_required && (
                                                <div className="mb-1 d-flex align-items-center">
                                                    <i className="bi bi-award me-2 text-primary opacity-75"></i>
                                                    <span className="text-truncate">{career.qualification_required}</span>
                                                </div>
                                            )}
                                            {career.experience_required && (
                                                <div className="mb-1 d-flex align-items-center">
                                                    <i className="bi bi-clock-history me-2 text-primary opacity-75"></i>
                                                    <span className="text-truncate">{career.experience_required}</span>
                                                </div>
                                            )}
                                            {(career.salary_min || career.salary_max) && (
                                                <div className="mb-1 d-flex align-items-center">
                                                    <i className="bi bi-currency-rupee me-2 text-primary opacity-75"></i>
                                                    <span>
                                                        {career.salary_min && career.salary_max 
                                                            ? `₹${Number(career.salary_min).toLocaleString()} - ₹${Number(career.salary_max).toLocaleString()}`
                                                            : career.salary_min 
                                                                ? `₹${Number(career.salary_min).toLocaleString()}+`
                                                                : `Up to ₹${Number(career.salary_max).toLocaleString()}`
                                                        }
                                                    </span>
                                                </div>
                                            )}
                                            {career.application_deadline && (
                                                <div className="d-flex align-items-center">
                                                    <i className="bi bi-calendar-event me-2 text-warning"></i>
                                                    <span>Deadline: {new Date(career.application_deadline).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-3 pt-2 border-top d-flex justify-content-end align-items-center">
                                             <button 
                                                className="btn btn-sm btn-outline-primary rounded-pill px-4 fw-semibold"
                                                onClick={() => setSelectedJob(career)}
                                            >
                                                View Details <i className="bi bi-arrow-right ms-1"></i>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-5">
                                 <i className="bi bi-briefcase display-1 text-light"></i>
                                 <p className="text-muted mt-3">No jobs found matching your criteria.</p>
                            </div>
                        )}
                    </div>
                )}

                 {/* Pagination */}
                 {!loading && lastPage > 1 && (
                    <div className="d-flex justify-content-center align-items-center mt-4 mb-5 pb-5 gap-3">
                        <button 
                            className={`btn rounded-circle shadow p-0 d-flex align-items-center justify-content-center ${currentPage === 1 ? 'btn-light text-muted opacity-50' : 'btn-primary text-white'}`}
                            style={{width: '40px', height: '40px'}}
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        <span className="fw-bold text-dark mx-2 small">
                            Page {currentPage} of {lastPage}
                        </span>
                        <button 
                            className={`btn rounded-circle shadow p-0 d-flex align-items-center justify-content-center ${currentPage === lastPage ? 'btn-light text-muted opacity-50' : 'btn-primary text-white'}`}
                            style={{width: '40px', height: '40px'}}
                            disabled={currentPage === lastPage}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                             <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Jobs;
